import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import UpgradeModal from "@/components/UpgradeModal";
import DashboardLayout from "@/components/DashboardLayout";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Overview from "@/pages/Overview";
import ProfilePage from "@/pages/ProfilePage";
import Discover from "@/pages/Discover";
import Applications from "@/pages/Applications";
import Communications from "@/pages/Communications";
import LoopSeries from "@/pages/LoopSeries";
import LoopDetail from "@/pages/LoopDetail";
import ManageCompanies from "@/pages/ManageCompanies";
import SavedCompanies from "@/pages/SavedCompanies";
import AdminSettings from "@/pages/AdminSettings";
import AdminEmailTemplates from "@/pages/AdminEmailTemplates";
import ContactAudit from "@/pages/ContactAudit";
import Pricing from "@/pages/Pricing";
import AdminPlans from "@/pages/AdminPlans";
import Billing from "@/pages/Billing";
import AdminBilling from "@/pages/AdminBilling";
import UserManagement from "@/pages/UserManagement";
import HelpSupport from "@/pages/HelpSupport";
import Docs from "@/pages/Docs";
import SsoPage from "@/pages/Sso";
import SsoCallback from "@/pages/SsoCallback";

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F6F8]">
      <div className="w-10 h-10 rounded-full border-2 border-orange-500/30 border-t-orange-500 animate-spin" />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const [adminState, setAdminState] = useState("checking");

  useEffect(() => {
    if (loading) return;
    if (!user) { setAdminState("guest"); return; }
    api.get("/admin/access")
      .then((r) => setAdminState(r.data.is_admin ? "ok" : "candidate"))
      .catch(() => setAdminState("candidate"));
  }, [user, loading]);

  if (loading || adminState === "checking") return <FullPageSpinner />;
  if (adminState === "guest") return <Navigate to="/auth" replace />;
  if (adminState === "candidate") return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app" replace />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/docs" element={<RequireAdmin><Docs /></RequireAdmin>} />
      <Route path="/sso" element={<SsoPage />} />
      <Route path="/sso-callback" element={<SsoCallback />} />
      <Route path="/dashboard" element={<Navigate to="/app" replace />} />
      <Route
        path="/app"
        element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Overview />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="discover" element={<Discover />} />
        <Route path="applications" element={<Applications />} />
        <Route path="communications" element={<Communications />} />
        <Route path="loops" element={<LoopSeries />} />
        <Route path="loops/:id" element={<LoopDetail />} />
        <Route path="companies" element={<ManageCompanies />} />
        <Route path="companies/saved" element={<SavedCompanies />} />
        <Route path="admin/settings" element={<AdminSettings />} />
        <Route path="admin/templates" element={<AdminEmailTemplates />} />
        <Route path="admin/contact-audit" element={<ContactAudit />} />
        <Route path="admin/plans" element={<AdminPlans />} />
        <Route path="admin/billing" element={<AdminBilling />} />
        <Route path="admin/users" element={<UserManagement />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="billing" element={<Billing />} />
        <Route path="help" element={<HelpSupport />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <UpgradeModal />
        </BrowserRouter>
        <Toaster position="top-right" richColors theme="light" />
      </AuthProvider>
    </div>
  );
}

export default App;
